import React, { useEffect, useMemo, useState } from 'react';
import '../assets/css/FinanciamentoSimulador.css';
// Versão branca só com o traço: o PNG original tem os quadrados preenchidos de branco, e um
// filtro de cor transformava tudo em blocos.
import logo from '../assets/img/logo61-branco.png';
import { BASE } from '../services/api';
import {
  COMPROMETIMENTO_RENDA,
  ENTRADA_MINIMA,
  PRAZOS,
  imovelQueCabe,
  moeda,
  simular,
  sugestoesEntrada,
} from '../services/financiamento';

// Simulação de parcelas e entrada (página pública /61Financiamento). Taxas médias do Banco
// Central pela rota pública do back; enquanto não chegam, a conta usa um valor de referência.
// O bloco "Quero uma análise de crédito" (WhatsApp com a simulação) saiu a pedido em 27/10/2026.

const TAXAS_PADRAO = { mercado: { taxa_aa: 13.5 }, regulada: { taxa_aa: 10.5 } };
const soDigitos = (v) => String(v || '').replace(/\D/g, '');
const pct = (v, casas = 0) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: casas })}%`;
const mesAno = (ref) => (ref ? ref.split('-').reverse().join('/') : null);

function CampoMoeda({ id, rotulo, valor, onChange, dica }) {
  return (
    <label className="fin-campo" htmlFor={id}>
      <span>{rotulo}</span>
      <input id={id} inputMode="numeric" placeholder="R$ 0" value={valor ? moeda(valor) : ''}
        onChange={(e) => onChange(Number(soDigitos(e.target.value)) || 0)} />
      {dica && <small>{dica}</small>}
    </label>
  );
}

function CalculadoraFinanciamento() {
  const inicial = useMemo(() => Number(new URLSearchParams(window.location.search).get('valor')) || 0, []);
  const [valorImovel, setValorImovel] = useState(inicial);
  const [entrada, setEntrada] = useState(inicial ? Math.round(inicial * ENTRADA_MINIMA) : 0);
  const [meses, setMeses] = useState(420);
  const [sistema, setSistema] = useState('SAC');
  const [renda, setRenda] = useState(0);
  const [taxas, setTaxas] = useState(TAXAS_PADRAO);
  const [tipoTaxa, setTipoTaxa] = useState('regulada');
  const [taxaPropria, setTaxaPropria] = useState('');

  useEffect(() => {
    let vivo = true;
    fetch(`${BASE}/portal-cliente/financiamento/taxas`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivo && d?.taxas?.mercado && d?.taxas?.regulada) setTaxas(d.taxas); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const taxa = tipoTaxa === 'propria'
    ? Number(String(taxaPropria).replace(',', '.')) || taxas.regulada.taxa_aa
    : taxas[tipoTaxa].taxa_aa;
  const r = simular({ valorImovel, entrada, taxaAnualPct: taxa, meses, sistema, renda });
  const sugestoes = sugestoesEntrada({ valorImovel, taxaAnualPct: taxa, meses, sistema });
  const cabe = renda ? imovelQueCabe({ renda, entrada, taxaAnualPct: taxa, meses, sistema }) : 0;
  const pronto = valorImovel > 0;

  const fonte = tipoTaxa !== 'propria' && taxas[tipoTaxa]?.referencia
    ? `Média do Banco Central, ${mesAno(taxas[tipoTaxa].referencia)}`
    : tipoTaxa === 'propria' ? 'Taxa informada por você' : 'Valor de referência';

  return (
    <main className="fin-page">
      <header className="fin-topo">
        <img src={logo} alt="61 Imóveis" className="fin-logo" />
        <div className="fin-topo-texto">
          <small>61 IMÓVEIS · FINANCIAMENTO</small>
          <h1>Simule o financiamento</h1>
          <p>Entrada, parcelas e renda necessária com as taxas médias do Banco Central.</p>
          <div className="fin-taxas" aria-label="Taxas médias do Banco Central">
            <span>SFH <strong>{taxas.regulada.taxa_aa.toLocaleString('pt-BR')}% a.a.</strong></span>
            <span>Mercado <strong>{taxas.mercado.taxa_aa.toLocaleString('pt-BR')}% a.a.</strong></span>
            {taxas.regulada.referencia && <span>Banco Central · {mesAno(taxas.regulada.referencia)}</span>}
          </div>
        </div>
      </header>

      <section className="fin-card fin-form" aria-label="Dados da simulação">
        <CampoMoeda id="fin-valor" rotulo="Valor do imóvel" valor={valorImovel}
          onChange={(v) => { setValorImovel(v); if (!entrada) setEntrada(Math.round(v * ENTRADA_MINIMA)); }} />
        <CampoMoeda id="fin-entrada" rotulo="Entrada (recursos próprios + FGTS)" valor={entrada} onChange={setEntrada}
          dica={pronto ? `${pct(r.percentualEntrada)} do imóvel · mínimo usual ${moeda(r.entradaMinima)} (20%)` : 'Mínimo usual: 20% do imóvel'} />
        <label className="fin-campo" htmlFor="fin-prazo">
          <span>Prazo</span>
          <select id="fin-prazo" value={meses} onChange={(e) => setMeses(Number(e.target.value))}>
            {PRAZOS.map((m) => <option key={m} value={m}>{m} meses ({m / 12} anos)</option>)}
          </select>
        </label>
        <div className="fin-campo">
          <span>Sistema de amortização</span>
          <div className="fin-alternar" role="radiogroup" aria-label="Sistema de amortização">
            {[['SAC', 'SAC', 'parcela cai todo mês'], ['PRICE', 'Price', 'parcela fixa']].map(([v, rot, dica]) => (
              <button type="button" key={v} role="radio" aria-checked={sistema === v}
                className={sistema === v ? 'is-on' : ''} onClick={() => setSistema(v)} title={dica}>{rot}</button>
            ))}
          </div>
          <small>{sistema === 'SAC' ? 'SAC: começa maior e cai todo mês; paga menos juros.' : 'Price: parcela igual do início ao fim.'}</small>
        </div>
        <label className="fin-campo" htmlFor="fin-taxa">
          <span>Taxa de juros</span>
          <select id="fin-taxa" value={tipoTaxa} onChange={(e) => setTipoTaxa(e.target.value)}>
            <option value="regulada">SFH · {taxas.regulada.taxa_aa.toLocaleString('pt-BR')}% a.a.</option>
            <option value="mercado">Mercado · {taxas.mercado.taxa_aa.toLocaleString('pt-BR')}% a.a.</option>
            <option value="propria">Outra taxa</option>
          </select>
          {tipoTaxa === 'propria' ? (
            <input aria-label="Taxa anual (%)" inputMode="decimal" placeholder="Ex.: 11,5" value={taxaPropria}
              onChange={(e) => setTaxaPropria(e.target.value.replace(/[^\d,.]/g, ''))} />
          ) : null}
          <small>{fonte}{tipoTaxa === 'regulada' ? ' · taxas reguladas (SFH)' : tipoTaxa === 'mercado' ? ' · média dos bancos' : ''}</small>
        </label>
        <CampoMoeda id="fin-renda" rotulo="Renda familiar bruta (opcional)" valor={renda} onChange={setRenda} />
      </section>

      {pronto ? (
        <>
          <section className="fin-resultados" aria-live="polite">
            <div className="fin-card fin-destaque">
              <span>{sistema === 'SAC' ? '1ª parcela' : 'Parcela'}</span>
              <strong>{moeda(r.primeira)}</strong>
              <small>{sistema === 'SAC' ? `cai até ${moeda(r.ultima)} na última` : `${meses} parcelas iguais`}</small>
            </div>
            <div className="fin-card">
              <span>Valor financiado</span>
              <strong>{moeda(r.financiado)}</strong>
              <small>{pct(1 - r.percentualEntrada)} do imóvel</small>
            </div>
            <div className="fin-card">
              <span>Renda mínima</span>
              <strong>{moeda(r.rendaMinima)}</strong>
              <small>parcela até {pct(COMPROMETIMENTO_RENDA)} da renda</small>
            </div>
            <div className="fin-card">
              <span>Total de juros</span>
              <strong>{moeda(r.juros)}</strong>
              <small>total pago {moeda(r.total + r.entrada)}</small>
            </div>
          </section>

          {r.entradaAbaixoDoMinimo && (
            <p className="fin-alerta">A entrada está abaixo de 20% do imóvel. A maioria dos bancos pede pelo menos {moeda(r.entradaMinima)}.</p>
          )}
          {r.comprometimento != null && (
            <p className={r.comprometimento > COMPROMETIMENTO_RENDA ? 'fin-alerta' : 'fin-ok'}>
              A 1ª parcela compromete {pct(r.comprometimento, 1)} da renda informada
              {r.comprometimento > COMPROMETIMENTO_RENDA
                ? `, acima dos ${pct(COMPROMETIMENTO_RENDA)} que os bancos aceitam. Com essa renda e essa entrada, cabe um imóvel de até ${moeda(cabe)}.`
                : `. Com essa renda e essa entrada, cabe um imóvel de até ${moeda(cabe)}.`}
            </p>
          )}

          <section className="fin-card">
            <h2>Sugestões de entrada</h2>
            <div className="fin-tabela-wrap">
              <table className="fin-tabela">
                <thead><tr><th>Entrada</th><th>Valor</th><th>Financiado</th><th>{sistema === 'SAC' ? '1ª parcela' : 'Parcela'}</th><th>Renda mínima</th></tr></thead>
                <tbody>
                  {sugestoes.map((s) => (
                    <tr key={s.percentual} className={Math.abs(s.percentualEntrada - r.percentualEntrada) < 0.005 ? 'is-atual' : ''}>
                      <td>{pct(s.percentual)}</td><td>{moeda(s.entrada)}</td><td>{moeda(s.financiado)}</td>
                      <td><strong>{moeda(s.primeira)}</strong></td><td>{moeda(s.rendaMinima)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : (
        <div className="fin-vazio">
          <strong>Comece pelo valor do imóvel</strong>
          <span>Mostramos a entrada mínima, a parcela, a renda necessária e sugestões de entrada.</span>
        </div>
      )}

      <p className="fin-nota">
        Simulação estimativa. Não inclui seguros obrigatórios (MIP e DFI), taxa de administração nem correção pela TR; o
        valor final e a aprovação dependem da análise do banco. Taxas: médias mensais do Banco Central para
        financiamento imobiliário de pessoa física (séries 20773 — SFH e 20772 — mercado).
      </p>
    </main>
  );
}

export default CalculadoraFinanciamento;
